<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-7">
        <div v-if="uiState === 'loading'" class="card shadow-sm border-0 rounded-custom p-5 text-center">
          <Spinner />
          <p class="small text-muted mt-3 mb-0">{{ $t("Preparing the CAM photo upload...") }}</p>
        </div>

        <div v-else-if="uiState === 'error'" class="card shadow-sm border-0 rounded-custom p-4">
          <div class="alert alert-danger mb-0">{{ errorMessage }}</div>
        </div>

        <div v-else-if="uiState === 'success'" class="card shadow-sm border-0 rounded-custom p-4">
          <div class="alert alert-success mb-0">
            {{ $t("Photo uploaded successfully. You can close this screen.") }}
          </div>
        </div>

        <div v-else class="card shadow-sm border-0 rounded-custom">
          <div class="card-body p-4 p-lg-5">
            <h2 class="text-violet text-uppercase mb-3">{{ $t("Upload CAM production photo") }}</h2>
            <p class="text-muted mb-4">
              {{ $t("Take or select a photo and confirm the upload.") }}
            </p>

            <div v-if="log?.material_name || log?.machine_name" class="alert alert-light border small">
              <div v-if="log?.material_name"><strong>{{ $t("Material") }}:</strong> {{ log.material_name }}</div>
              <div v-if="log?.machine_name"><strong>{{ $t("Machine") }}:</strong> {{ log.machine_name }}</div>
            </div>

            <label class="form-label fw-semibold">{{ $t("Production photo") }}</label>
            <Dropzone
              :upload="upload"
              :max-files="1"
              :multi-upload="false"
              :upload-through-qrcode="true"
              :assign-url-s3="assignUrlS3"
              @increment="onFileAdded"
              @decrease="onFileRemoved"
              @s3-url-upload-completed="completedUpload"
            >
              <img src="@/assets/icons/upload-violet.svg" width="70" alt="upload" />
              <div class="mt-3">{{ $t("Upload a photo of the milling") }}</div>
            </Dropzone>

            <div v-if="submitError" class="alert alert-danger">
              {{ submitError }}
            </div>

            <button
              class="btn btn-violet text-uppercase w-100"
              :disabled="mediaCounter === 0 || isSubmitting"
              @click="submit"
            >
              {{ isSubmitting ? $t("Sending...") : $t("Confirm photo upload") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Dropzone from "@/components/upload/Dropzone";
import Spinner from "@/components/general/Spinner";
import {
  assignPhotoUploadUrl,
  completePhotoUpload,
  showPhotoUpload,
} from "@/use/repositories/camProduction";
import { firstErrorMessage } from "@/use/utilities/camProduction/firstErrorMessage";

export default {
  name: "CamProductionPhotoUpload",
  components: {
    Dropzone,
    Spinner,
  },
  setup() {
    const i18n = useI18n();
    const route = useRoute();
    const token = route.params.token;

    const uiState = ref("loading");
    const errorMessage = ref("");
    const submitError = ref("");
    const upload = ref(false);
    const isSubmitting = ref(false);
    const mediaCounter = ref(0);
    const uploadedFilename = ref("");
    const log = ref(null);

    const loadUploadTarget = async () => {
      uiState.value = "loading";
      errorMessage.value = "";

      try {
        const { data } = await showPhotoUpload(token, false);
        log.value = data?.log ?? null;
        uiState.value = "ready";
      } catch (error) {
        errorMessage.value = firstErrorMessage(error, i18n.t("CAM photo link is not available."));
        uiState.value = "error";
      }
    };

    const assignUrlS3 = async (filename) => {
      try {
        const { data } = await assignPhotoUploadUrl(
          token,
          {
            filename,
          },
          false,
        );

        return { assignUrl: data };
      } catch (error) {
        const message = firstErrorMessage(
          error,
          i18n.t("CAM photo link expired. Regenerate the QR code from the CAM workstation."),
        );
        submitError.value = message;
        upload.value = false;
        isSubmitting.value = false;
        throw new Error(message);
      }
    };

    const onFileAdded = () => {
      mediaCounter.value = 1;
      submitError.value = "";
    };

    const onFileRemoved = () => {
      mediaCounter.value = 0;
      uploadedFilename.value = "";
      submitError.value = "";
    };

    const completedUpload = async (fileNames) => {
      try {
        uploadedFilename.value = Array.isArray(fileNames) ? fileNames[0] : "";

        if (!uploadedFilename.value) {
          throw new Error(i18n.t("Invalid file name."));
        }

        const { log: updatedLog } = await completePhotoUpload(
          token,
          {
            filename: uploadedFilename.value,
          },
          false,
        );

        log.value = updatedLog ?? null;
        uiState.value = "success";
      } catch (error) {
        submitError.value = firstErrorMessage(
          error,
          i18n.t("An error occurred while saving the CAM photo."),
        );
        isSubmitting.value = false;
        upload.value = false;
      }
    };

    const submit = () => {
      submitError.value = "";

      if (mediaCounter.value === 0) {
        submitError.value = i18n.t("A photo is required for this step.");
        return;
      }

      isSubmitting.value = true;
      upload.value = true;
    };

    onMounted(() => {
      loadUploadTarget();
    });

    return {
      uiState,
      errorMessage,
      submitError,
      upload,
      isSubmitting,
      mediaCounter,
      log,
      assignUrlS3,
      onFileAdded,
      onFileRemoved,
      completedUpload,
      submit,
    };
  },
};
</script>
